import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { LookConfig, LookStep, ConditionRule } from '../types/look-config'
import { fetchLookConfig, submitLookAnswers } from '../api/look'
import type { LookSubmitRequest } from '../api/look'

export const useLookModal = defineStore('lookModal', () => {
  const lookModal = ref({
    isShow: false,
    currentStepIndex: 0,
    isLoading: false,
    isLoadingConfig: false,
    config: null as LookConfig | null,
    lookData: {} as Record<string, any>,
    capsuleId: null as number | null
  })

  const steps = computed(() => lookModal.value.config?.steps ?? [])
  const currentStep = computed(() => steps.value[lookModal.value.currentStepIndex])
  const totalSteps = computed(() => steps.value.length)

  const getCurrentStep = (): LookStep | undefined => {
    return currentStep.value
  }

  const getStepById = (stepId: string): LookStep | undefined => {
    return steps.value.find(step => step.id === stepId)
  }

  const getStepIndexById = (stepId: string): number => {
    return steps.value.findIndex(step => step.id === stepId)
  }

  const evaluateConditionRule = (rule: ConditionRule, lookData: Record<string, any>): boolean => {
    const fieldValue = lookData[rule.field]

    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value
      case 'not_equals':
        return fieldValue !== rule.value
      case 'greater_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue > rule.value
      case 'less_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue < rule.value
      case 'greater_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue >= rule.value
      case 'less_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue <= rule.value
      case 'in':
        return Array.isArray(rule.value) && rule.value.includes(fieldValue)
      case 'not_in':
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue)
      case 'exists':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
      case 'not_exists':
        return fieldValue === null || fieldValue === undefined || fieldValue === ''
      default:
        return false
    }
  }

  const shouldStepBeVisible = (step: LookStep): boolean => {
    if (!step.conditional) return true

    const condition = step.conditional
    const data = lookModal.value.lookData

    // Поддержка старого формата для обратной совместимости
    if (condition.field && condition.value !== undefined) {
      return data[condition.field] === condition.value
    }

    // Новый формат с правилами
    if (condition.rules && condition.rules.length > 0) {
      const logic = condition.logic || 'AND'

      if (logic === 'AND') {
        return condition.rules.every(rule => evaluateConditionRule(rule, data))
      } else {
        return condition.rules.some(rule => evaluateConditionRule(rule, data))
      }
    }

    return true
  }

  const findNextVisibleStepIndex = (startIndex: number, direction: 'forward' | 'backward'): number => {
    if (direction === 'forward') {
      for (let i = startIndex + 1; i < steps.value.length; i++) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    } else {
      for (let i = startIndex - 1; i >= 0; i--) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    }
    return -1
  }

  const loadLookConfig = async (config: LookConfig) => {
    lookModal.value.config = config
    lookModal.value.lookData = {}
    lookModal.value.capsuleId = null

    // Находим первый видимый шаг
    const firstVisibleIndex = findNextVisibleStepIndex(-1, 'forward')
    lookModal.value.currentStepIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : 0
  }

  const validateCurrentStep = (): boolean => {
    const step = currentStep.value
    if (!step) return false

    if (step.required === false) return true

    if (step.type === 'occasion') {
      const value = lookModal.value.lookData[step.id]
      return value !== null && value !== undefined && value !== ''
    }

    if (step.type === 'dynamic_form') {
      // Проверяем все обязательные поля
      for (const field of step.fields) {
        if (field.required) {
          const fieldKey = `${step.id}_${field.id}`
          const value = lookModal.value.lookData[fieldKey]
          if (value === null || value === undefined || value === '') {
            return false
          }
        }
      }
      return true
    }

    if (step.type === 'tinder_like') {
      // Для tinder_like проверяем, что все карточки были обработаны
      const stepKey = `${step.id}_cardIndex`
      const currentCardIndex = lookModal.value.lookData[stepKey]
      const cardsCount = step.cardsCount
      
      if (!step.required) return true
      
      return typeof currentCardIndex === 'number' && currentCardIndex >= cardsCount
    }

    return true
  }

  const showLookModal = async () => {
    lookModal.value.isShow = true
    lookModal.value.isLoadingConfig = true

    try {
      const config = await fetchLookConfig()
      await loadLookConfig(config)
    } catch (error) {
      console.error('Error loading look config:', error)
    } finally {
      lookModal.value.isLoadingConfig = false
    }
  }

  const hideLookModal = () => {
    lookModal.value.isShow = false
  }

  const nextStep = () => {
    if (!validateCurrentStep()) {
      return
    }

    const nextIndex = findNextVisibleStepIndex(lookModal.value.currentStepIndex, 'forward')
    if (nextIndex >= 0) {
      lookModal.value.currentStepIndex = nextIndex
    }
  }

  const prevStep = () => {
    const prevIndex = findNextVisibleStepIndex(lookModal.value.currentStepIndex, 'backward')
    if (prevIndex >= 0) {
      lookModal.value.currentStepIndex = prevIndex
    }
  }

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < totalSteps.value) {
      const step = steps.value[stepIndex]
      // Если шаг не видим, находим ближайший видимый
      if (step && !shouldStepBeVisible(step)) {
        const visibleIndex = findNextVisibleStepIndex(stepIndex, 'forward')
        if (visibleIndex >= 0) {
          lookModal.value.currentStepIndex = visibleIndex
        } else {
          // Если вперед нет видимых, ищем назад
          const visibleIndexBack = findNextVisibleStepIndex(stepIndex, 'backward')
          if (visibleIndexBack >= 0) {
            lookModal.value.currentStepIndex = visibleIndexBack
          }
        }
      } else {
        lookModal.value.currentStepIndex = stepIndex
      }
    }
  }

  const goToStepById = (stepId: string) => {
    const index = getStepIndexById(stepId)
    if (index >= 0) {
      goToStep(index)
    }
  }

  const updateLookData = (key: string, value: any) => {
    lookModal.value.lookData[key] = value

    // Если текущий шаг стал невидимым из-за изменения данных, переходим к следующему видимому
    const currentStepValue = currentStep.value
    if (currentStepValue && !shouldStepBeVisible(currentStepValue)) {
      const nextVisibleIndex = findNextVisibleStepIndex(lookModal.value.currentStepIndex, 'forward')
      if (nextVisibleIndex >= 0) {
        lookModal.value.currentStepIndex = nextVisibleIndex
      } else {
        // Если вперед нет видимых, ищем назад
        const prevVisibleIndex = findNextVisibleStepIndex(lookModal.value.currentStepIndex, 'backward')
        if (prevVisibleIndex >= 0) {
          lookModal.value.currentStepIndex = prevVisibleIndex
        }
      }
    }
  }

  const submitLookSurvey = async () => {
    lookModal.value.isLoading = true
    try {
      const config = lookModal.value.config
      if (!config) {
        throw new Error('Look config is not loaded')
      }

      // Формируем данные для отправки на бекенд
      const submitData: LookSubmitRequest = {
        lookConfigId: config.id,
        answers: { ...lookModal.value.lookData },
        metadata: {
          completedAt: new Date().toISOString(),
          deviceType: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop',
          language: 'ru'
        }
      }

      // Отправляем данные на бекенд
      const result = await submitLookAnswers(submitData)

      // Сохраняем capsuleId в store
      lookModal.value.capsuleId = result.capsuleId
    } catch (error) {
      console.error('Error submitting look survey:', error)
      throw error
    } finally {
      lookModal.value.isLoading = false
    }
  }

  return {
    lookModal,
    steps,
    currentStep,
    totalSteps,
    getCurrentStep,
    getStepById,
    getStepIndexById,
    shouldStepBeVisible,
    findNextVisibleStepIndex,
    loadLookConfig,
    validateCurrentStep,
    showLookModal,
    hideLookModal,
    nextStep,
    prevStep,
    goToStep,
    goToStepById,
    updateLookData,
    submitLookSurvey
  }
})

export const useLookModalStore = useLookModal

